import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { toast } from 'sonner'
import { compressImage } from '@/shared/lib/compress-image'
import { ExampleGallery } from '@/shared/components/example-gallery'
import { CaptureButton } from './capture-button'
import { UploadThumbnail } from './upload-thumbnail'
import { DocumentOptionPicker } from './document-option-picker'
import type { Slot, UploadedFile } from './types'
import type { SessionUploadParams } from '@/shared/lib/evidence/session-api'

const MAX_FILE_SIZE = 15 * 1024 * 1024 // 15MB
const UNDO_DELETE_WINDOW_MS = 5000

interface LocalUpload {
  id: string
  previewUrl: string
  filename: string
  mimeType: string
  progress: number
}

export interface EvidenceSlotCardProps {
  slot: Slot
  index: number
  error?: string
  /** Show the per-file delete affordance. Requires `deleteFn`. Default false. */
  canDelete?: boolean
  /** Uploads a file into the slot and resolves with the stored file. */
  uploadFn: (params: SessionUploadParams) => Promise<UploadedFile>
  /** Removes a stored file. Only used when `canDelete` is true. */
  deleteFn?: (fileUuid: string) => Promise<void>
}

/**
 * One requirement "slot" of an evidence-request session: label + completion
 * badge, helper text, example photos, uploaded thumbnails, and the capture
 * controls. The upload/delete transport is injected, so the same card serves
 * the standalone Upload Evidence page (with delete) and the consumer claim
 * evidence checklist (upload-but-not-delete) - one implementation, two surfaces
 * (WPS-138 #6).
 *
 * Self-sufficient display: a file uploaded this session is shown immediately
 * from local state and de-duplicated once it reappears on `slot.uploads`, so
 * the card does not depend on how each surface refreshes the session.
 */
export function EvidenceSlotCard({
  slot,
  index,
  error,
  canDelete = false,
  uploadFn,
  deleteFn,
}: EvidenceSlotCardProps) {
  const { t } = useTranslation()
  const [localUploads, setLocalUploads] = useState<LocalUpload[]>([])
  // Files uploaded this session, kept until they reappear on slot.uploads.
  const [done, setDone] = useState<UploadedFile[]>([])
  // Object URL per uploaded file uuid, so the photo never re-downloads.
  const [previewByUuid, setPreviewByUuid] = useState<Record<string, string>>({})
  const createdUrlsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const urls = createdUrlsRef.current
    return () => urls.forEach((url) => URL.revokeObjectURL(url))
  }, [])
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set())
  const [docOption, setDocOption] = useState<string>(
    slot.options.find((o) => o.preferred)?.key || slot.options[0]?.key || '',
  )

  const mergedUploads = [
    ...slot.uploads,
    ...done.filter((d) => !slot.uploads.some((s) => s.uuid === d.uuid)),
  ]
  const visibleUploads = mergedUploads.filter((f) => !pendingDeleteIds.has(f.uuid))
  const uploadCount = visibleUploads.length + localUploads.length
  const isComplete = slot.min > 0 && visibleUploads.length >= slot.min
  const isMaxed = slot.max !== null && uploadCount >= slot.max
  const showDelete = canDelete && !!deleteFn

  const handleFiles = useCallback(
    async (files: File[]) => {
      for (const original of files) {
        const file = await compressImage(original)
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('evidence.fileTooLarge', 'File is too large (max 15MB)'))
          continue
        }

        const localId = crypto.randomUUID()
        const previewUrl = URL.createObjectURL(file)
        createdUrlsRef.current.add(previewUrl)

        setLocalUploads((prev) => [
          ...prev,
          {
            id: localId,
            previewUrl,
            filename: file.name,
            mimeType: file.type,
            progress: 0,
          },
        ])

        try {
          const uploaded = await uploadFn({
            slotId: slot.id,
            file,
            documentOptionKey: slot.kind === 'document' ? docOption : undefined,
            onProgress: ({ percent }) =>
              setLocalUploads((prev) =>
                prev.map((u) => (u.id === localId ? { ...u, progress: percent } : u)),
              ),
          })
          setPreviewByUuid((prev) => ({ ...prev, [uploaded.uuid]: previewUrl }))
          setDone((prev) =>
            prev.some((d) => d.uuid === uploaded.uuid) ? prev : [...prev, uploaded],
          )
          setLocalUploads((prev) => prev.filter((u) => u.id !== localId))
        } catch {
          toast.error(t('evidence.uploadFailed', 'Upload failed. Please try again.'))
          setLocalUploads((prev) => prev.filter((u) => u.id !== localId))
          URL.revokeObjectURL(previewUrl)
        }
      }
    },
    [uploadFn, slot.id, slot.kind, docOption, t],
  )

  const handleDelete = useCallback(
    (fileUuid: string) => {
      if (!deleteFn) return
      setPendingDeleteIds((prev) => new Set(prev).add(fileUuid))

      const clearPending = () =>
        setPendingDeleteIds((prev) => {
          const next = new Set(prev)
          next.delete(fileUuid)
          return next
        })

      const commit = async () => {
        setDeletingIds((prev) => new Set(prev).add(fileUuid))
        try {
          await deleteFn(fileUuid)
          setDone((prev) => prev.filter((d) => d.uuid !== fileUuid))
          setPreviewByUuid((prev) => {
            if (!prev[fileUuid]) return prev
            URL.revokeObjectURL(prev[fileUuid])
            const rest = { ...prev }
            delete rest[fileUuid]
            return rest
          })
        } catch {
          toast.error(t('evidence.deleteFailed', 'Could not remove file'))
          clearPending()
        } finally {
          setDeletingIds((prev) => {
            const next = new Set(prev)
            next.delete(fileUuid)
            return next
          })
        }
      }

      const timeoutId = window.setTimeout(commit, UNDO_DELETE_WINDOW_MS)

      toast(t('evidence.photoRemoved', 'Photo removed'), {
        action: {
          label: t('common.undo', 'Undo'),
          onClick: () => {
            window.clearTimeout(timeoutId)
            clearPending()
          },
        },
        duration: UNDO_DELETE_WINDOW_MS,
      })
    },
    [deleteFn, t],
  )

  const badgeText =
    slot.min > 0 ? `${visibleUploads.length} of ${slot.min}` : `${visibleUploads.length}`

  return (
    <div
      className={`panel-arrive overflow-hidden rounded-lg border-[0.5px] shadow-[var(--shadow-float)] transition-colors duration-300 ${
        error
          ? 'border-[var(--status-error-border)] bg-[var(--status-error-bg)]'
          : isComplete
            ? 'border-[var(--green-border)] bg-[var(--green-bg-subtle)]'
            : 'border-[var(--rule-strong)] bg-[var(--bg)]'
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="p-4">
        <div className="mb-2 flex items-start justify-between gap-3">
          <h3 className="clara-heading text-[18px]">{slot.label}</h3>
          <div
            className={`flex flex-none items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium tracking-[0.04em] transition-colors duration-300 ${
              isComplete
                ? 'bg-[var(--green-bg)] text-[var(--green)]'
                : visibleUploads.length > 0
                  ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                  : 'bg-[var(--status-info-bg)] text-[var(--ink-3)]'
            }`}
          >
            {isComplete && (
              <Check size={10} className="animate-in zoom-in-0 duration-200" />
            )}
            {badgeText}
          </div>
        </div>

        {slot.description && (
          <p className="clara-body mb-3 text-[var(--ink-2)]">{slot.description}</p>
        )}

        <ExampleGallery urls={slot.example_media_urls} label={slot.label} />

        {(visibleUploads.length > 0 || localUploads.length > 0) && (
          <div className="mb-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {visibleUploads.map((file) => (
              <UploadThumbnail
                key={file.uuid}
                src={previewByUuid[file.uuid] || file.thumbnail_url || file.url}
                filename={file.original_filename}
                mimeType={file.mime_type}
                onDelete={showDelete ? () => handleDelete(file.uuid) : undefined}
                isDeleting={deletingIds.has(file.uuid)}
              />
            ))}
            {localUploads.map((local) => (
              <UploadThumbnail
                key={local.id}
                src={local.previewUrl}
                filename={local.filename}
                mimeType={local.mimeType}
                progress={local.progress}
              />
            ))}
          </div>
        )}

        {slot.kind === 'document' && slot.options.length > 0 && (
          <DocumentOptionPicker
            options={slot.options}
            selected={docOption}
            onChange={setDocOption}
          />
        )}

        {isMaxed ? (
          <p className="clara-small text-center text-[var(--ink-3)]">
            {t('evidence.maxFilesUploaded', 'Maximum files uploaded')}
          </p>
        ) : (
          <CaptureButton kind={slot.kind} onFiles={handleFiles} />
        )}

        {error && (
          <p className="mt-2 text-[12px] text-[var(--status-error)]">{error}</p>
        )}
      </div>
    </div>
  )
}
